import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDarkMode } from '../../App';
import StatsSection from '../../components/feature/StatsSection';

export default function Home() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [portfolioOpen, setPortfolioOpen] = useState(false);
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const [activeTab, setActiveTab] = useState<'vision' | 'create' | 'elevate'>('vision');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    interests: [] as string[],
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle checkbox changes
  const handleCheckboxChange = (interest: string) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.fullName || !formData.email) {
      setSubmitStatus('error');
      return;
    }

    if (formData.message.length > 500) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Prepare form data in application/x-www-form-urlencoded format
      const formBody = new URLSearchParams();
      formBody.append('fullName', formData.fullName);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('interests', formData.interests.join(', '));
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5gjqu452fie682gh7e0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        // Reset form
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          interests: [],
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      // Reset status after 5 seconds
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }
  };

  // Scroll animation observer
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in-up');
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll('.scroll-animate');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#1a1a1a] text-white' : 'bg-white text-[#000000]'} transition-colors duration-500`} style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Top Right Icons - Same as Landing Page */}
      <div className="fixed top-4 right-4 md:top-8 md:right-8 flex items-center gap-3 md:gap-4 z-50">
        {/* Dark Mode Toggle */}
        <button
          onClick={toggleDarkMode}
          className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center ${isDarkMode ? 'text-white/70 hover:text-white' : 'text-[#000000]/70 hover:text-[#000000]'} transition-colors cursor-pointer`}
        >
          <i className={`ri-${isDarkMode ? 'moon' : 'sun'}-line text-lg md:text-xl`}></i>
        </button>

        {/* Menu Toggle */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center ${isDarkMode ? 'text-white/70 hover:text-white' : 'text-[#000000]/70 hover:text-[#000000]'} transition-colors cursor-pointer`}
        >
          <i className="ri-menu-line text-lg md:text-xl"></i>
        </button>
      </div>

      {/* Slide-out Menu */}
      <div className={`fixed top-0 right-0 h-full w-72 md:w-80 ${isDarkMode ? 'bg-[#0d0d0d]' : 'bg-[#F2F2F2]'} shadow-2xl transform transition-transform duration-500 z-40 ${menuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="p-6 md:p-8">
          {/* Close Button */}
          <button
            onClick={() => setMenuOpen(false)}
            className={`absolute top-6 right-6 md:top-8 md:right-8 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center ${isDarkMode ? 'text-white/70 hover:text-white' : 'text-[#000000]/70 hover:text-[#000000]'} transition-colors cursor-pointer`}
          >
            <i className="ri-close-line text-lg md:text-xl"></i>
          </button>

          {/* Menu Content */}
          <nav className="mt-16 md:mt-20 space-y-5 md:space-y-6">
            <Link 
              to="/home" 
              onClick={() => setMenuOpen(false)}
              className={`block text-sm md:text-base font-normal tracking-[0.15em] uppercase ${isDarkMode ? 'text-white/90 hover:text-white' : 'text-[#000000]/90 hover:text-[#000000]'} transition-colors`}
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Home
            </Link>
            <a 
              href="https://order.cinestate.ca/booking/new"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMenuOpen(false)}
              className={`block text-sm md:text-base font-normal tracking-[0.15em] uppercase ${isDarkMode ? 'text-white/90 hover:text-white' : 'text-[#000000]/90 hover:text-[#000000]'} transition-colors`}
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Listing Packages
            </a>
            <Link 
              to="/packages" 
              onClick={() => setMenuOpen(false)}
              className={`block text-sm md:text-base font-normal tracking-[0.15em] uppercase ${isDarkMode ? 'text-white/90 hover:text-white' : 'text-[#000000]/90 hover:text-[#000000]'} transition-colors`}
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Content Packages
            </Link>
            <Link 
              to="/staging" 
              onClick={() => setMenuOpen(false)}
              className={`block text-sm md:text-base font-normal tracking-[0.15em] uppercase ${isDarkMode ? 'text-white/90 hover:text-white' : 'text-[#000000]/90 hover:text-[#000000]'} transition-colors`}
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Home Staging
            </Link>
            {/* Portfolio Dropdown */}
            <div>
              <button
                onClick={() => setPortfolioOpen(!portfolioOpen)}
                className={`flex items-center justify-between w-full text-sm md:text-base font-normal tracking-[0.15em] uppercase ${isDarkMode ? 'text-white/90 hover:text-white' : 'text-[#000000]/90 hover:text-[#000000]'} transition-colors cursor-pointer`}
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                <span>Portfolio</span>
                <i className={`${portfolioOpen ? 'ri-subtract-line' : 'ri-add-line'} text-xs`}></i>
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${portfolioOpen ? 'max-h-24 mt-3' : 'max-h-0'}`}>
                <div className="ml-4 space-y-3">
                  <Link
                    to="/portfolio"
                    onClick={() => setMenuOpen(false)}
                    className={`block text-xs md:text-sm font-normal tracking-[0.15em] uppercase ${isDarkMode ? 'text-white/55 hover:text-white' : 'text-[#000000]/55 hover:text-[#000000]'} transition-colors`}
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    Residential
                  </Link>
                  <Link
                    to="/portfolio/commercial"
                    onClick={() => setMenuOpen(false)}
                    className={`block text-xs md:text-sm font-normal tracking-[0.15em] uppercase ${isDarkMode ? 'text-white/55 hover:text-white' : 'text-[#000000]/55 hover:text-[#000000]'} transition-colors`}
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    Commercial
                  </Link>
                </div>
              </div>
            </div>
            <a 
              href="#contact" 
              onClick={() => setMenuOpen(false)}
              className={`block text-sm md:text-base font-normal tracking-[0.15em] uppercase ${isDarkMode ? 'text-white/90 hover:text-white' : 'text-[#000000]/90 hover:text-[#000000]'} transition-colors`}
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Contact
            </a>
          </nav>

          {/* Social Links */}
          <div className="absolute bottom-8 md:bottom-12 left-6 md:left-8 flex gap-3 md:gap-4">
            <a href="https://www.instagram.com/kianikanstudio?igsh=M3RoZ25wc25oZHY0" target="_blank" rel="noopener noreferrer" className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center border ${isDarkMode ? 'border-white/20 text-white/70 hover:border-white hover:text-white' : 'border-[#000000]/20 text-[#000000]/70 hover:border-[#000000] hover:text-[#000000]'} transition-colors`}>
              <i className="ri-instagram-line text-sm md:text-base"></i>
            </a>
            <a href="https://youtube.com/@kianikanstudio3017?si=QvudTCaWtHR5bmy8" target="_blank" rel="noopener noreferrer" className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center border ${isDarkMode ? 'border-white/20 text-white/70 hover:border-white hover:text-white' : 'border-[#000000]/20 text-[#000000]/70 hover:border-[#000000] hover:text-[#000000]'} transition-colors`}>
              <i className="ri-youtube-line text-sm md:text-base"></i>
            </a>
            <a href="https://www.tiktok.com/@kianikanstudio.ca?_r=1&_t=ZS-91yzSCjzfEY" target="_blank" rel="noopener noreferrer" className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center border ${isDarkMode ? 'border-white/20 text-white/70 hover:border-white hover:text-white' : 'border-[#000000]/20 text-[#000000]/70 hover:border-[#000000] hover:text-[#000000]'} transition-colors`}>
              <i className="ri-tiktok-line text-sm md:text-base"></i>
            </a>
            <a href="https://www.instagram.com/kianikanstudio?igsh=M3RoZ25wc25oZHY0" target="_blank" rel="noopener noreferrer" className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center border ${isDarkMode ? 'border-white/20 text-white/70 hover:border-white hover:text-white' : 'border-[#000000]/20 text-[#000000]/70 hover:border-[#000000] hover:text-[#000000]'} transition-colors`}>
              <i className="ri-facebook-fill text-sm md:text-base"></i>
            </a>
          </div>
        </div>
      </div>

      {/* Overlay when menu is open */}
      {menuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30"
          onClick={() => setMenuOpen(false)}
        ></div>
      )}

      {/* Logo - Top Left */}
      <div className="fixed top-4 left-4 md:top-8 md:left-8 z-40">
        <Link to="/" className="block">
          <img 
            src="https://static.readdy.ai/image/530c8f921e7d182176cffe22debeaa7c/91365b59b7e8e66ad4ab2980732fafc0.png"
            alt="CINÉSTATE"
            className={`h-5 md:h-7 w-auto ${isDarkMode ? 'brightness-0 invert' : ''}`}
          />
        </Link>
      </div>

      {/* Hero Section - Split Cards */}
      <section className="min-h-screen pt-16 md:pt-24 scroll-animate">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 min-h-[calc(100vh-4rem)] md:min-h-[calc(100vh-6rem)]">
          {/* Listing Packages Card */}
          <a href="https://order.cinestate.ca/booking/new" target="_blank" rel="noopener noreferrer" className="group relative overflow-hidden cursor-pointer">
            <div className="absolute inset-0">
              <img 
                src="https://static.readdy.ai/image/530c8f921e7d182176cffe22debeaa7c/07999d18e5b5db61d88dfdd0961d70d2.jpeg"
                alt="Listing Packages"
                className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors duration-500"></div>
            </div>
            <div className="relative h-full min-h-[300px] md:min-h-[400px] lg:min-h-[600px] flex flex-col justify-end p-6 md:p-8 text-white">
              <h3 className="text-2xl md:text-3xl lg:text-4xl font-normal tracking-wide mb-3 md:mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>LISTING PACKAGES</h3>
              <div className="flex items-center gap-2 text-xs md:text-sm tracking-widest uppercase opacity-0 group-hover:opacity-100 transition-opacity duration-500 font-normal">
                <span>Explore</span>
                <i className="ri-arrow-right-line"></i>
              </div>
            </div>
          </a>

          {/* Content Packages Card */}
          <Link to="/packages" className="group relative overflow-hidden cursor-pointer">
            <div className="absolute inset-0">
              <img 
                src="https://static.readdy.ai/image/530c8f921e7d182176cffe22debeaa7c/cb43ea8dd4cd40516cf706833376dd91.jpeg"
                alt="Content Packages"
                className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors duration-500"></div>
            </div>
            <div className="relative h-full min-h-[300px] md:min-h-[400px] lg:min-h-[600px] flex flex-col justify-end p-6 md:p-8 text-white">
              <div className="text-[10px] md:text-xs tracking-widest uppercase mb-2 text-white/70 font-normal">MONTHLY PLANS</div>
              <h3 className="text-2xl md:text-3xl lg:text-4xl font-normal tracking-wide mb-3 md:mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>CONTENT PACKAGES</h3>
              <div className="flex items-center gap-2 text-xs md:text-sm tracking-widest uppercase opacity-0 group-hover:opacity-100 transition-opacity duration-500 font-normal">
                <span>View Plans</span>
                <i className="ri-arrow-right-line"></i>
              </div>
            </div>
          </Link>

          {/* Home Staging Card */}
          <Link to="/staging" className="group relative overflow-hidden cursor-pointer">
            <div className="absolute inset-0">
              <img 
                src="https://static.readdy.ai/image/530c8f921e7d182176cffe22debeaa7c/e65600385e3a53d151df641bbb00c8f6.jpeg"
                alt="Home Staging"
                className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors duration-500"></div>
            </div>
            <div className="relative h-full min-h-[300px] md:min-h-[400px] lg:min-h-[600px] flex flex-col justify-end p-6 md:p-8 text-white">
              <h3 className="text-2xl md:text-3xl lg:text-4xl font-normal tracking-wide mb-3 md:mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>HOME STAGING</h3>
              <div className="flex items-center gap-2 text-xs md:text-sm tracking-widest uppercase opacity-0 group-hover:opacity-100 transition-opacity duration-500 font-normal">
                <span>Explore</span>
                <i className="ri-arrow-right-line"></i>
              </div>
            </div>
          </Link>

          {/* Contact Us Card */}
          <a href="#contact" className="group relative overflow-hidden cursor-pointer">
            <div className="absolute inset-0">
              <img 
                src="https://static.readdy.ai/image/530c8f921e7d182176cffe22debeaa7c/a86d51de65a89279b720c55ec9581714.jpeg"
                alt="Contact Us"
                className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors duration-500"></div>
            </div>
            <div className="relative h-full min-h-[300px] md:min-h-[400px] lg:min-h-[600px] flex flex-col justify-end p-6 md:p-8 text-white">
              <h3 className="text-2xl md:text-3xl lg:text-4xl font-normal tracking-wide mb-3 md:mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>CONTACT US</h3>
              <div className="flex items-center gap-2 text-xs md:text-sm tracking-widest uppercase opacity-0 group-hover:opacity-100 transition-opacity duration-500 font-normal">
                <span>Get In Touch</span>
                <i className="ri-arrow-right-line"></i>
              </div>
            </div>
          </a>
        </div>
      </section>

      {/* Tagline Section */}
      <section id="about" className="py-16 md:py-24 lg:py-32 px-6 md:px-8 lg:px-16 scroll-animate">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-24">
            <h2 className="text-xl md:text-2xl lg:text-3xl font-normal leading-relaxed tracking-wide" style={{ fontFamily: "'Inter', sans-serif" }}>
              WE CRAFT STANDOUT VISUALS THAT MOVE BRANDS — AND MOVE REAL ESTATE.
            </h2>
            <p className={`${isDarkMode ? 'text-white/70' : 'text-[#000000]/70'} text-base md:text-lg leading-relaxed font-light`} style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              At Cinéstate, we elevate real estate and brand marketing through thoughtful storytelling and refined content. Every project is driven by purpose, crafted with care, and built to stand out in a crowded market.
            </p>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className={`w-full h-px ${isDarkMode ? 'bg-white/10' : 'bg-[#000000]/10'}`}></div>

      {/* Video Banner - starts at 9 seconds */}
      <section className="relative h-[40vh] md:h-[60vh] overflow-hidden scroll-animate">
        <iframe
          src="https://www.youtube.com/embed/_zZm3C4dXqw?autoplay=1&mute=1&loop=1&playlist=_zZm3C4dXqw&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&start=9"
          className="absolute top-1/2 left-1/2 w-[200%] h-[200%] -translate-x-1/2 -translate-y-1/2 pointer-events-none"
          style={{ minWidth: '100%', minHeight: '100%' }}
          allow="autoplay; encrypted-media"
          allowFullScreen
        ></iframe>
        <div className={`absolute inset-0 ${isDarkMode ? 'bg-black/20' : 'bg-white/20'}`}></div>
      </section>

      {/* Clients Section */}
      <section className="py-16 md:py-24 lg:py-32 px-6 md:px-8 lg:px-16 scroll-animate">
        <StatsSection />
      </section>

      {/* Divider */}
      <div className={`w-full h-px ${isDarkMode ? 'bg-white/10' : 'bg-[#000000]/10'}`}></div>

      {/* Services Section */}
      <section id="services" className="py-16 md:py-24 lg:py-32 px-6 md:px-8 lg:px-16 scroll-animate">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 lg:gap-24">
            {/* Left Side - Large Text Display */}
            <div className="relative flex items-center justify-center min-h-[300px] md:min-h-[500px] lg:min-h-[700px]">
              <h3 
                className={`text-5xl md:text-7xl lg:text-8xl xl:text-9xl font-light tracking-wide ${isDarkMode ? 'text-white' : 'text-[#000000]'}`}
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                {activeTab === 'vision' && 'VISION'}
                {activeTab === 'create' && 'CREATE'}
                {activeTab === 'elevate' && 'ELEVATE'}
              </h3>
            </div>

            {/* Right Side - Content */}
            <div className="flex flex-col justify-center">
              <div className="mb-8 md:mb-12">
                <div className={`flex gap-6 md:gap-8 mb-8 md:mb-12 border-b ${isDarkMode ? 'border-white/10' : 'border-[#000000]/10'}`}>
                  <button 
                    onClick={() => setActiveTab('vision')}
                    className={`pb-3 md:pb-4 text-xs md:text-sm tracking-widest uppercase font-normal transition-colors ${
                      activeTab === 'vision' 
                        ? `border-b-2 ${isDarkMode ? 'border-white' : 'border-[#000000]'}` 
                        : `${isDarkMode ? 'text-white/50 hover:text-white' : 'text-[#000000]/50 hover:text-[#000000]'}`
                    }`}
                  >
                    Vision
                  </button>
                  <button 
                    onClick={() => setActiveTab('create')}
                    className={`pb-3 md:pb-4 text-xs md:text-sm tracking-widest uppercase font-normal transition-colors ${
                      activeTab === 'create' 
                        ? `border-b-2 ${isDarkMode ? 'border-white' : 'border-[#000000]'}` 
                        : `${isDarkMode ? 'text-white/50 hover:text-white' : 'text-[#000000]/50 hover:text-[#000000]'}`
                    }`}
                  >
                    Create
                  </button>
                  <button 
                    onClick={() => setActiveTab('elevate')}
                    className={`pb-3 md:pb-4 text-xs md:text-sm tracking-widest uppercase font-normal transition-colors ${
                      activeTab === 'elevate' 
                        ? `border-b-2 ${isDarkMode ? 'border-white' : 'border-[#000000]'}` 
                        : `${isDarkMode ? 'text-white/50 hover:text-white' : 'text-[#000000]/50 hover:text-[#000000]'}`
                    }`}
                  >
                    Elevate
                  </button>
                </div>

                {/* Vision Content */}
                {activeTab === 'vision' && (
                  <div>
                    <h4 className="text-lg md:text-xl font-normal mb-4 md:mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>Building the Blueprint</h4>
                    <p className={`${isDarkMode ? 'text-white/70' : 'text-[#000000]/70'} text-sm md:text-base leading-relaxed mb-6 md:mb-8 font-light`} style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      At Cinéstate, we don't just produce content — we design narratives that elevate real estate and brand presence. From strategic planning to cinematic execution, every step is shaped by innovation, precision, and purpose.
                    </p>
                    <p className={`${isDarkMode ? 'text-white/70' : 'text-[#000000]/70'} text-sm md:text-base leading-relaxed mb-6 md:mb-8 font-light`} style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      We start by understanding the intention behind the project. Through creative strategy, scripting, scheduling, and visual planning, we build a foundation that ensures the final content feels cohesive, elevated, and impossible to overlook.
                    </p>
                  </div>
                )}

                {/* Create Content */}
                {activeTab === 'create' && (
                  <div>
                    <h4 className="text-lg md:text-xl font-normal mb-4 md:mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>Creating the Visual Experience</h4>
                    <p className={`${isDarkMode ? 'text-white/70' : 'text-[#000000]/70'} text-sm md:text-base leading-relaxed mb-6 md:mb-8 font-light`} style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      This is where the vision becomes real. Our team uses cinematic techniques, purposeful movement, and expert lighting to capture a property's essence — not just its features. The result: content that feels alive, immersive, and unmistakably premium.
                    </p>
                  </div>
                )}

                {/* Elevate Content */}
                {activeTab === 'elevate' && (
                  <div>
                    <h4 className="text-lg md:text-xl font-normal mb-4 md:mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>Final Touch, Lasting Impact</h4>
                    <p className={`${isDarkMode ? 'text-white/70' : 'text-[#000000]/70'} text-sm md:text-base leading-relaxed mb-6 md:mb-8 font-light`} style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      From color grading to pacing and sound design, every detail is shaped with purpose. Our post-production team crafts videos that spark attention, strengthen brand identity, and leave a lasting impression.
                    </p>
                  </div>
                )}

                <a href="#contact" className="inline-flex items-center gap-2 text-xs md:text-sm tracking-widest uppercase hover:gap-4 transition-all font-normal cursor-pointer">
                  <span>Contact Us</span>
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className={`w-full h-px ${isDarkMode ? 'bg-white/10' : 'bg-[#000000]/10'}`}></div>

      {/* Services List */}
      <section className="py-16 md:py-24 lg:py-32 px-6 md:px-8 lg:px-16 scroll-animate">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 md:gap-16">
            <div>
              <h3 className="text-xs md:text-sm tracking-widest uppercase mb-6 md:mb-8 font-normal">Our Services</h3>
              <Link to="/packages" className={`inline-flex items-center gap-2 px-5 py-2.5 md:px-6 md:py-3 border ${isDarkMode ? 'border-white text-white hover:bg-white hover:text-[#000000]' : 'border-[#000000] text-[#000000] hover:bg-[#000000] hover:text-white'} text-xs md:text-sm tracking-widest uppercase transition-colors font-normal whitespace-nowrap cursor-pointer`}>
                View Packages
              </Link>
            </div>

            <div className="lg:col-span-2 space-y-8 md:space-y-12">
              <div className={`border-b ${isDarkMode ? 'border-white/10' : 'border-[#000000]/10'} pb-8 md:pb-12`}>
                <h4 className="text-lg md:text-xl font-normal mb-3 md:mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>Videography</h4>
                <p className={`${isDarkMode ? 'text-white/70' : 'text-[#000000]/70'} text-sm md:text-base leading-relaxed font-light`} style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Our videography blends design, storytelling, and cinematic technique to showcase real estate at its absolute best. Every video is crafted to feel intentional, elevated, and visually compelling.
                </p>
              </div>

              <div className={`border-b ${isDarkMode ? 'border-white/10' : 'border-[#000000]/10'} pb-8 md:pb-12`}>
                <h4 className="text-lg md:text-xl font-normal mb-3 md:mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>Photography</h4>
                <p className={`${isDarkMode ? 'text-white/70' : 'text-[#000000]/70'} text-sm md:text-base leading-relaxed font-light`} style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Our photographers use refined composition and a deep understanding of space to create imagery that cuts through the noise. The result: elevated, high-impact photos that define the feel of the property.
                </p>
              </div>

              <div className={`border-b ${isDarkMode ? 'border-white/10' : 'border-[#000000]/10'} pb-8 md:pb-12`}>
                <h4 className="text-lg md:text-xl font-normal mb-3 md:mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>Drone Coverage</h4>
                <p className={`${isDarkMode ? 'text-white/70' : 'text-[#000000]/70'} text-sm md:text-base leading-relaxed font-light`} style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Professional aerial shots for listings and community storytelling. Our drone footage 
                  captures the full scope of properties and their surroundings, providing stunning 
                  perspectives that ground-level shots simply cannot achieve.
                </p>
              </div>

              <div>
                <h4 className="text-lg md:text-xl font-normal mb-3 md:mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>Social Media Content</h4>
                <p className={`${isDarkMode ? 'text-white/70' : 'text-[#000000]/70'} text-sm md:text-base leading-relaxed font-light`} style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  We craft social media content that feels natural but performs like a campaign. Clean cuts, strong storytelling, and consistent output designed to grow your brand.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className={`w-full h-px ${isDarkMode ? 'bg-white/10' : 'bg-[#000000]/10'}`}></div>

      {/* Contact Section */}
      <section id="contact" className="py-16 md:py-24 lg:py-32 px-6 md:px-8 lg:px-16 scroll-animate">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 lg:gap-24">
            {/* Contact Info */}
            <div>
              <h2 className="text-xl md:text-2xl font-normal mb-8 md:mb-12" style={{ fontFamily: "'Inter', sans-serif" }}>Contact Us</h2>
              
              <div className="space-y-5 md:space-y-6 mb-8 md:mb-12">
                <div className="flex items-center gap-3 md:gap-4">
                  <div className="w-4 h-4 md:w-5 md:h-5 flex items-center justify-center">
                    <i className="ri-mail-line text-xs md:text-sm"></i>
                  </div>
                  <a href="mailto:info@cinestate.ca" className={`${isDarkMode ? 'text-white/70 hover:text-white' : 'text-[#000000]/70 hover:text-[#000000]'} text-sm md:text-base transition-colors font-light`} style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    info@cinestate.ca
                  </a>
                </div>
                <div className="flex items-center gap-3 md:gap-4">
                  <div className="w-4 h-4 md:w-5 md:h-5 flex items-center justify-center">
                    <i className="ri-phone-line text-xs md:text-sm"></i>
                  </div>
                  <a href="tel:+16475551234" className={`${isDarkMode ? 'text-white/70 hover:text-white' : 'text-[#000000]/70 hover:text-[#000000]'} text-sm md:text-base transition-colors font-light`} style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    (416) 837-7225
                  </a>
                </div>
                <div className="flex items-center gap-3 md:gap-4">
                  <div className="w-4 h-4 md:w-5 md:h-5 flex items-center justify-center">
                    <i className="ri-map-pin-line text-xs md:text-sm"></i>
                  </div>
                  <span className={`${isDarkMode ? 'text-white/70' : 'text-[#000000]/70'} text-sm md:text-base font-light`} style={{ fontFamily: "'Cormorant Garamond', serif" }}>Toronto, Ontario, Canada</span>
                </div>
              </div>

              <div className="flex gap-3 md:gap-4">
                <a href="https://www.instagram.com/kianikanstudio?igsh=M3RoZ25wc25oZHY0" target="_blank" rel="noopener noreferrer" className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center border ${isDarkMode ? 'border-white/20 hover:border-white' : 'border-[#000000]/20 hover:border-[#000000]'} transition-colors cursor-pointer`}>
                  <i className="ri-instagram-line text-sm md:text-base"></i>
                </a>
                <a href="https://youtube.com/@kianikanstudio3017?si=QvudTCaWtHR5bmy8" target="_blank" rel="noopener noreferrer" className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center border ${isDarkMode ? 'border-white/20 hover:border-white' : 'border-[#000000]/20 hover:border-[#000000]'} transition-colors cursor-pointer`}>
                  <i className="ri-youtube-line text-sm md:text-base"></i>
                </a>
                <a href="https://www.tiktok.com/@kianikanstudio.ca?_r=1&_t=ZS-91yzSCjzfEY" target="_blank" rel="noopener noreferrer" className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center border ${isDarkMode ? 'border-white/20 hover:border-white' : 'border-[#000000]/20 hover:border-[#000000]'} transition-colors cursor-pointer`}>
                  <i className="ri-tiktok-line text-sm md:text-base"></i>
                </a>
                <a href="https://www.instagram.com/kianikanstudio?igsh=M3RoZ25wc25oZHY0" target="_blank" rel="noopener noreferrer" className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center border ${isDarkMode ? 'border-white/20 hover:border-white' : 'border-[#000000]/20 hover:border-[#000000]'} transition-colors cursor-pointer`}>
                  <i className="ri-facebook-fill text-sm md:text-base"></i>
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <form 
                id="d5gjqu452fie682gh7e0" 
                data-readdy-form 
                onSubmit={handleSubmit} 
                className="space-y-5 md:space-y-6"
              >
                <div>
                  <label className="block text-xs md:text-sm tracking-widest uppercase mb-2 md:mb-3 font-normal">Full Name</label>
                  <input 
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                    className={`w-full bg-transparent border ${isDarkMode ? 'border-white/20 text-white placeholder-white/50 focus:border-white' : 'border-[#000000]/20 text-[#000000] placeholder-[#000000]/50 focus:border-[#000000]'} px-3 py-2.5 md:px-4 md:py-3 text-sm md:text-base focus:outline-none transition-colors`}
                    placeholder=""
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                  <div>
                    <label className="block text-xs md:text-sm tracking-widest uppercase mb-2 md:mb-3 font-normal">Email</label>
                    <input 
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className={`w-full bg-transparent border ${isDarkMode ? 'border-white/20 text-white placeholder-white/50 focus:border-white' : 'border-[#000000]/20 text-[#000000] placeholder-[#000000]/50 focus:border-[#000000]'} px-3 py-2.5 md:px-4 md:py-3 text-sm md:text-base focus:outline-none transition-colors`}
                      placeholder=""
                    />
                  </div>
                  <div>
                    <label className="block text-xs md:text-sm tracking-widest uppercase mb-2 md:mb-3 font-normal">Phone</label>
                    <input 
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`w-full bg-transparent border ${isDarkMode ? 'border-white/20 text-white placeholder-white/50 focus:border-white' : 'border-[#000000]/20 text-[#000000] placeholder-[#000000]/50 focus:border-[#000000]'} px-3 py-2.5 md:px-4 md:py-3 text-sm md:text-base focus:outline-none transition-colors`}
                      placeholder=""
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs md:text-sm tracking-widest uppercase mb-2 md:mb-3 font-normal">What are you interested in?</label>
                  <div className="grid grid-cols-2 gap-3 md:gap-4">
                    {['Listing Packages', 'Content Packages', 'Home Staging', 'Other'].map((item) => (
                      <label key={item} className="flex items-center gap-2 md:gap-3 cursor-pointer group">
                        <div className={`w-3.5 h-3.5 md:w-4 md:h-4 border ${isDarkMode ? 'border-white/40 group-hover:border-white' : 'border-[#000000]/40 group-hover:border-[#000000]'} transition-colors flex items-center justify-center`}>
                          <input 
                            type="checkbox" 
                            className="sr-only peer"
                            checked={formData.interests.includes(item)}
                            onChange={() => handleCheckboxChange(item)}
                          />
                          <div className={`w-1.5 h-1.5 md:w-2 md:h-2 ${isDarkMode ? 'bg-white' : 'bg-[#000000]'} scale-0 peer-checked:scale-100 transition-transform`}></div>
                        </div>
                        <span className={`text-xs md:text-sm ${isDarkMode ? 'text-white/70 group-hover:text-white' : 'text-[#000000]/70 group-hover:text-[#000000]'} transition-colors font-light`} style={{ fontFamily: "'Cormorant Garamond', serif" }}>{item}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs md:text-sm tracking-widest uppercase mb-2 md:mb-3 font-normal">Message</label>
                  <textarea 
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    maxLength={500}
                    className={`w-full bg-transparent border ${isDarkMode ? 'border-white/20 text-white placeholder-white/50 focus:border-white' : 'border-[#000000]/20 text-[#000000] placeholder-[#000000]/50 focus:border-[#000000]'} px-3 py-2.5 md:px-4 md:py-3 text-sm md:text-base focus:outline-none transition-colors resize-none`}
                    placeholder="Type your message..."
                  ></textarea>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-white/50' : 'text-[#000000]/50'}`}>
                    {formData.message.length}/500 characters
                  </p>
                </div>

                {/* Submit Status Messages */}
                {submitStatus === 'success' && (
                  <div className="p-3 bg-green-500/20 border border-green-500/50 rounded text-sm text-green-500">
                    Thank you! Your message has been sent successfully.
                  </div>
                )}
                
                {submitStatus === 'error' && (
                  <div className="p-3 bg-red-500/20 border border-red-500/50 rounded text-sm text-red-500">
                    Please fill in all required fields and ensure message is under 500 characters.
                  </div>
                )}

                <button 
                  type="submit"
                  disabled={isSubmitting}
                  className={`px-6 py-2.5 md:px-8 md:py-3 ${isDarkMode ? 'bg-white text-[#000000] hover:bg-white/90' : 'bg-[#000000] text-white hover:bg-[#000000]/90'} text-xs md:text-sm tracking-widest uppercase transition-colors whitespace-nowrap cursor-pointer font-normal disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={`border-t ${isDarkMode ? 'border-white/10' : 'border-[#000000]/10'}`}>
        <div className="px-6 md:px-8 lg:px-16 py-12 md:py-16">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 md:gap-8 mb-8 md:mb-12">
              <Link to="/" className="block">
                <img 
                  src="https://static.readdy.ai/image/530c8f921e7d182176cffe22debeaa7c/91365b59b7e8e66ad4ab2980732fafc0.png"
                  alt="CINÉSTATE"
                  className={`h-5 md:h-7 w-auto ${isDarkMode ? 'brightness-0 invert' : ''}`}
                />
              </Link>
              
              <div className="flex flex-wrap gap-5 md:gap-8">
                <a href="https://order.cinestate.ca/booking/new" target="_blank" rel="noopener noreferrer" className={`text-xs md:text-sm tracking-widest uppercase ${isDarkMode ? 'text-white/70 hover:text-white' : 'text-[#000000]/70 hover:text-[#000000]'} transition-colors font-normal`}>Listing Packages</a>
                <Link to="/packages" className={`text-xs md:text-sm tracking-widest uppercase ${isDarkMode ? 'text-white/70 hover:text-white' : 'text-[#000000]/70 hover:text-[#000000]'} transition-colors font-normal`}>Content Packages</Link>
                <Link to="/staging" className={`text-xs md:text-sm tracking-widest uppercase ${isDarkMode ? 'text-white/70 hover:text-white' : 'text-[#000000]/70 hover:text-[#000000]'} transition-colors font-normal`}>Home Staging</Link>
                <Link to="/portfolio" className={`text-xs md:text-sm tracking-widest uppercase ${isDarkMode ? 'text-white/70 hover:text-white' : 'text-[#000000]/70 hover:text-[#000000]'} transition-colors font-normal`}>Portfolio</Link>
                <a href="#contact" className={`text-xs md:text-sm tracking-widest uppercase ${isDarkMode ? 'text-white/70 hover:text-white' : 'text-[#000000]/70 hover:text-[#000000]'} transition-colors font-normal`}>Contact</a>
              </div>

              <div className="flex gap-3 md:gap-4">
                <a href="https://www.instagram.com/kianikanstudio?igsh=M3RoZ25wc25oZHY0" target="_blank" rel="noopener noreferrer" className={`w-7 h-7 md:w-8 md:h-8 flex items-center justify-center ${isDarkMode ? 'text-white/70 hover:text-white' : 'text-[#000000]/70 hover:text-[#000000]'} transition-colors cursor-pointer`}>
                  <i className="ri-instagram-line text-sm md:text-base"></i>
                </a>
                <a href="https://youtube.com/@kianikanstudio3017?si=QvudTCaWtHR5bmy8" target="_blank" rel="noopener noreferrer" className={`w-7 h-7 md:w-8 md:h-8 flex items-center justify-center ${isDarkMode ? 'text-white/70 hover:text-white' : 'text-[#000000]/70 hover:text-[#000000]'} transition-colors cursor-pointer`}>
                  <i className="ri-youtube-line text-sm md:text-base"></i>
                </a>
                <a href="https://www.tiktok.com/@kianikanstudio.ca?_r=1&_t=ZS-91yzSCjzfEY" target="_blank" rel="noopener noreferrer" className={`w-7 h-7 md:w-8 md:h-8 flex items-center justify-center ${isDarkMode ? 'text-white/70 hover:text-white' : 'text-[#000000]/70 hover:text-[#000000]'} transition-colors cursor-pointer`}>
                  <i className="ri-tiktok-line text-sm md:text-base"></i>
                </a>
                <a href="https://www.instagram.com/kianikanstudio?igsh=M3RoZ25wc25oZHY0" target="_blank" rel="noopener noreferrer" className={`w-7 h-7 md:w-8 md:h-8 flex items-center justify-center ${isDarkMode ? 'text-white/70 hover:text-white' : 'text-[#000000]/70 hover:text-[#000000]'} transition-colors cursor-pointer`}>
                  <i className="ri-facebook-fill text-sm md:text-base"></i>
                </a>
              </div>
            </div>

            <div className={`w-full h-px ${isDarkMode ? 'bg-white/10' : 'bg-[#000000]/10'} mb-6 md:mb-8`}></div>

            <div className={`flex flex-col md:flex-row justify-between items-center gap-3 md:gap-4 text-xs md:text-sm ${isDarkMode ? 'text-white/50' : 'text-[#000000]/50'}`}>
              <p className="font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>© 2025 Cinéstate. All rights reserved.</p>
              <a href="https://readdy.ai/?origin=logo" className={`${isDarkMode ? 'hover:text-white' : 'hover:text-[#000000]'} transition-colors font-light`} style={{ fontFamily: "'Cormorant Garamond', serif" }}>Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
